import type { GeolocationRepository } from '@/core/data/location/geolocation.repository';
import { GeoDataSource, type UserGeoData, type UserGeoResponse } from '@/core/data/location/location.interface';
import { gambits } from '@/core/gambits';
import { createLogger } from '@/core/helpers/logger.helper';
import { useAuthStore } from '@/stores/auth.store';
import { useGeolocationStore } from '@/stores/geolocation.store';
import { debounce } from 'lodash';

const logger = createLogger('GeolocationService');

const WATCHDOG_INTERVAL_MS = 30_000;
const WATCHDOG_STALE_THRESHOLD_MS = 45_000;

const onLocationAcquired = debounce(
  (position: GeolocationPosition) => {
    const { latitude, longitude, accuracy } = position.coords;
    const store = useGeolocationStore();

    const existing = store.userLocation;
    if (existing?.source === GeoDataSource.BROWSER_API && existing.updatedAt) {
      const existingAge = Date.now() - new Date(existing.updatedAt).getTime();
      const positionAge = position.timestamp ? Date.now() - position.timestamp : 0;
      if (positionAge > existingAge) return;
    }

    store.setLocationPermissionGranted(true);
    store.onUserLocationChanged({
      location: { lat: latitude, lng: longitude },
      accuracy: accuracy,
      source: GeoDataSource.BROWSER_API,
      updatedAt: new Date().toISOString(),
    });
  },
  200,
  { leading: false },
);

export class GeolocationService {
  private readonly repository: GeolocationRepository;
  private watchId: number | undefined;
  private visibilityHandler: (() => void) | undefined;
  private watchdogTimer: ReturnType<typeof setInterval> | undefined;

  constructor(repository: GeolocationRepository) {
    this.repository = repository;
  }

  saveGeoLocation(geoData: UserGeoData): UserGeoData {
    this.repository.setUserLocation(geoData);

    if (useAuthStore().isAuthenticated() && useAuthStore().user?.isActive) {
      gambits.userService.updateLocation(geoData);
    }

    return geoData;
  }

  getLastLocation(): UserGeoData | null {
    return this.repository.getUserLocation();
  }

  async getLocationFromIp(): Promise<UserGeoResponse | null> {
    const result = await gambits.userService.getIpLocation();

    if (result.error || !result.data) return null;

    const lat = result.data.latitude;
    const lng = result.data.longitude;

    return {
      accuracy: 200,
      location: { lat, lng },
      source: GeoDataSource.CLOUDFLARE_IP,
      updatedAt: new Date().toISOString(),
      response: result.data,
    };
  }

  async getLocationFromCountry(): Promise<UserGeoData | null> {
    const result = await gambits.userService.getLocationFromCountry();

    if (result.error || !result.data) return null;

    return {
      accuracy: 100000,
      location: {
        lat: result.data.latitude,
        lng: result.data.longitude,
      },
      source: GeoDataSource.CLOUDFLARE_COUNTRY,
      updatedAt: new Date().toISOString(),
    };
  }

  async getCurrentPosition() {
    if (!navigator.geolocation) {
      logger.error('Geolocation is not supported by this browser.');
    }

    navigator.geolocation.getCurrentPosition(
      onLocationAcquired,
      (error) => {
        logger.error('Error getting current geolocation:', error);
        if (error.code === GeolocationPositionError.PERMISSION_DENIED) {
          // Retry without enableHighAccuracy — handles precise location blocked
          navigator.geolocation.getCurrentPosition(
            onLocationAcquired,
            (retryError) => {
              logger.error('Retry geolocation also failed:', retryError);
            },
            { enableHighAccuracy: false, maximumAge: 0, timeout: 10000 },
          );
        }
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 10000,
      },
    );
  }

  async startTracking() {
    if (!navigator.geolocation) {
      logger.error('Geolocation is not supported by this browser.');
    }

    if (this.watchId) {
      logger.info('Already tracking');
    }

    this.watchId = navigator.geolocation.watchPosition(
      onLocationAcquired,
      (error) => {
        logger.error('Error watching user geolocation:', error);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 5000,
      },
    );

    logger.info('Started tracking', this.watchId);

    if (!this.visibilityHandler) {
      this.visibilityHandler = () => {
        if (document.visibilityState === 'visible' && this.watchId) {
          logger.info('App resumed — forcing fresh position');
          this.getCurrentPosition();
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }

    this.startWatchdog();
  }

  private startWatchdog() {
    if (this.watchdogTimer) return;
    this.watchdogTimer = setInterval(() => {
      if (!this.watchId) return;
      const store = useGeolocationStore();
      const lastUpdate = store.userLocation?.updatedAt;
      const elapsed = lastUpdate ? Date.now() - new Date(lastUpdate).getTime() : Infinity;
      if (elapsed > WATCHDOG_STALE_THRESHOLD_MS) {
        logger.info(`Watchdog: no GPS update for ${Math.round(elapsed / 1000)}s — restarting watcher`);
        navigator.geolocation.clearWatch(this.watchId!);
        this.watchId = navigator.geolocation.watchPosition(
          onLocationAcquired,
          (error) => {
            logger.error('Error watching user geolocation:', error);
          },
          {
            enableHighAccuracy: true,
            maximumAge: 5000,
          },
        );
        this.getCurrentPosition();
      }
    }, WATCHDOG_INTERVAL_MS);
  }

  isTracking() {
    return this.watchId !== undefined;
  }

  stopTracking() {
    if (!this.watchId) return;
    navigator.geolocation.clearWatch(this.watchId);
    this.watchId = undefined;

    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = undefined;
    }

    if (this.watchdogTimer) {
      clearInterval(this.watchdogTimer);
      this.watchdogTimer = undefined;
    }
  }
}
